import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

// Pages fetched at the same time when viewing all
const VIEW_ALL_CONCURRENCY = 4;

/**
 * Pagination controls that append the following pages to the section's
 * `[data-pagination-list]` instead of navigating. "Load more" adds the next page;
 * "View all" adds every remaining page. Without a list, "Load more" stays a plain link.
 *
 * @extends {Component}
 */
class PaginationLoadMore extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncViewAll();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncViewAll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Appends the next page.
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    // Keep the native link navigation when the list can't be extended in place
    if (!this.#list || !this.#sectionId) return;

    event.preventDefault();
    await this.#load([this.#currentPage + 1]);
  }

  /**
   * Appends every remaining page.
   * @param {MouseEvent} event
   */
  async viewAll(event) {
    event.preventDefault();

    const pages = [];
    for (let page = this.#currentPage + 1; page <= this.#lastPage; page++) pages.push(page);

    await this.#load(pages);
  }

  /**
   * @param {number[]} pages - Page numbers to append, in order.
   */
  async #load(pages) {
    const list = this.#list;
    const sectionId = this.#sectionId;
    if (!list || !sectionId || !pages.length || this.hasAttribute('loading')) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const hadFocus = this.contains(document.activeElement);

    this.setAttribute('loading', '');
    list.setAttribute('aria-busy', 'true');

    try {
      const documents = await this.#fetchPages(sectionId, pages, signal);
      if (signal.aborted) return;

      /** @type {Element | undefined} */
      let firstNewItem;

      for (const doc of documents) {
        const items = Array.from(doc.querySelector('[data-pagination-list]')?.children ?? []);
        firstNewItem ??= items[0];
        list.append(...items);
      }

      this.#updateControls(documents.at(-1));

      if (hadFocus) this.#restoreFocus(firstNewItem);
    } catch (error) {
      if (signal.aborted) return;

      // Fall back to a regular page load so the shopper still reaches the next items
      console.warn('[pagination-load-more] Could not load page, navigating instead:', error);
      window.location.assign(this.#pageUrl(pages[0]).toString());
    } finally {
      if (!signal.aborted) {
        this.removeAttribute('loading');
        list.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Fetches pages in small batches and parses them, preserving page order.
   * @param {string} sectionId
   * @param {number[]} pages
   * @param {AbortSignal} signal
   * @returns {Promise<Document[]>}
   */
  async #fetchPages(sectionId, pages, signal) {
    const parser = new DOMParser();
    /** @type {Document[]} */
    const documents = [];

    for (let index = 0; index < pages.length; index += VIEW_ALL_CONCURRENCY) {
      const batch = pages.slice(index, index + VIEW_ALL_CONCURRENCY);
      const htmls = await Promise.all(
        batch.map((page) => sectionRenderer.getSectionHTML(sectionId, true, this.#pageUrl(page), signal))
      );

      if (signal.aborted) return documents;

      for (const html of htmls) documents.push(parser.parseFromString(html, 'text/html'));
    }

    return documents;
  }

  /**
   * Takes the status and actions rendered for the last fetched page. The status
   * element is kept and only its text changes, so its live region announces the update.
   * @param {Document | undefined} doc
   */
  #updateControls(doc) {
    const source = doc?.querySelector('pagination-load-more');
    if (!(source instanceof HTMLElement)) return;

    this.dataset.currentPage = source.dataset.currentPage ?? this.dataset.currentPage;

    const status = this.querySelector('.pagination__status');
    const sourceStatus = source.querySelector('.pagination__status');
    if (status && sourceStatus) status.textContent = sourceStatus.textContent;

    const actions = this.querySelector('.pagination__actions');
    const sourceActions = source.querySelector('.pagination__actions');
    if (actions && sourceActions) actions.replaceWith(sourceActions);

    this.#syncViewAll();
  }

  /**
   * Keeps keyboard focus in place after the actions are replaced.
   * @param {Element | undefined} firstNewItem
   */
  #restoreFocus(firstNewItem) {
    const loadMore = this.querySelector('.pagination__load-more');
    if (loadMore instanceof HTMLElement) {
      loadMore.focus();
      return;
    }

    // Nothing left to load: continue from the first item that was added
    const target = firstNewItem?.querySelector('a[href], button:not([disabled])') ?? firstNewItem;
    if (!(target instanceof HTMLElement)) return;

    if (target === firstNewItem && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
  }

  #syncViewAll() {
    const viewAll = this.querySelector('.pagination__view-all');
    if (viewAll instanceof HTMLElement) viewAll.hidden = !this.#list || !this.#sectionId;
  }

  /**
   * @param {number} page
   */
  #pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set(this.dataset.pageParam || 'page', `${page}`);
    url.hash = '';

    return url;
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #sectionId() {
    return this.#section?.id.replace(/^shopify-section-/, '') || null;
  }

  get #list() {
    return this.#section?.querySelector('[data-pagination-list]') ?? null;
  }

  get #currentPage() {
    return Number(this.dataset.currentPage) || 1;
  }

  get #lastPage() {
    return Number(this.dataset.lastPage) || 1;
  }
}

if (!customElements.get('pagination-load-more')) {
  customElements.define('pagination-load-more', PaginationLoadMore);
}
